// src/constants/communityGames.js
// ============================================================================
// COMMUNITY GAMES (founder 2026-08-12) — the open catalog, frontend mirror.
//
// Every game is playable on CritCash, not just the ones vision has been
// calibrated for. Three tiers of game identity:
//   1. GAME_PROFILES keys     — full profiles (formats, modes, vision).
//   2. EXTRA_STREAM_GAMES keys — catalog games with box art but no full
//                                profile; they get a generic event profile.
//   3. cg_* community keys    — free-text games typed by users ("Other").
//
// Games WITHOUT vision resolution run manualResults: players report their
// own results after the match. The Match Ready flow is identical for every
// tier.
//
// MIRROR: functions/constants/communityGames.js — keep in sync.
// ============================================================================

import { GAME_PROFILES } from './gameData';
import { EXTRA_STREAM_GAMES } from './streamGames';
import { COMMUNITY_GAME_CATALOG } from './communityCatalog';

export const EXTRA_EVENT_GAMES = EXTRA_STREAM_GAMES;

export const COMMUNITY_GAME_PREFIX = 'cg_';
export const COMMUNITY_GAME_NAME_MAX = 50;
// Full key must satisfy the backend GAME_KEY_RE ([a-z0-9_]{1,32}).
export const COMMUNITY_GAME_KEY_RE = /^cg_[a-z0-9_]{1,29}$/;

// Games where vision determines outcomes on its own — mirror of the backend
// VISION_RESOLVE_GAMES list.
export const VISION_RESOLVE_GAMES = new Set(['mdn', 'gt7', 'cod', 'sf6']);

export const COMMUNITY_ALLOWED_MODES = ['h2h', 'bracket'];

export const COMMUNITY_ALLOWED_PLATFORMS = [
  'PC', 'PS5', 'PS4', 'Xbox Series X|S', 'Xbox One', 'Nintendo Switch', 'Cross-Platform',
];

// Genres a community game can declare — mirrors the platform's category
// pills plus 'rts' (Strategy) and a catch-all. Backend mirror in
// functions/constants/communityGames.js.
export const COMMUNITY_GENRES = [
  { key: 'fighting', label: 'Fighting' },
  { key: 'shooter', label: 'Shooter' },
  { key: 'sports', label: 'Sports' },
  { key: 'racing', label: 'Racing' },
  { key: 'moba', label: 'MOBA' },
  { key: 'rts', label: 'Strategy' },
  { key: 'other', label: 'Other' },
];
const COMMUNITY_GENRE_KEYS = new Set(COMMUNITY_GENRES.map((g) => g.key));

export function normalizeCommunityGenre(raw) {
  const key = String(raw || '').toLowerCase().trim();
  return COMMUNITY_GENRE_KEYS.has(key) ? key : 'other';
}

// Own-property lookup: bare bracket access would treat prototype names
// ('constructor', 'toString') as valid catalog keys.
function extraGameFor(key) {
  return Object.prototype.hasOwnProperty.call(EXTRA_EVENT_GAMES, key || '')
    ? EXTRA_EVENT_GAMES[key]
    : null;
}

export function normalizeCommunityGameName(raw) {
  return String(raw || '')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[<>&"'`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, COMMUNITY_GAME_NAME_MAX);
}

// The ONE slugger — the catalog lookup below depends on byte-identical
// slugging. Mirror of the backend's communityGameSlug.
function communityGameSlug(raw) {
  const name = normalizeCommunityGameName(raw);
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 29)
    .replace(/_+$/g, '');
  return slug || null;
}

// COMMUNITY GAME CATALOG lookup (founder 2026-08-13): every catalog entry's
// name and aliases slug to ONE canonical key — "Halo" and "halo infinite"
// land on the same game with the right genre; "mario kart" lands on mkw.
const CATALOG_BY_SLUG = (() => {
  const map = new Map();
  for (const entry of COMMUNITY_GAME_CATALOG) {
    const nameSlug = communityGameSlug(entry.name);
    if (!nameSlug) continue;
    const resolved = {
      key: entry.key || `${COMMUNITY_GAME_PREFIX}${nameSlug}`,
      name: entry.name,
      genre: entry.genre || null,
    };
    for (const alias of [entry.name, ...(entry.aliases || [])]) {
      const slug = communityGameSlug(alias);
      if (slug) map.set(slug, resolved);
    }
  }
  return map;
})();

// The catalog entry a STORED cg_* key canonically belongs to — alias slugs
// never remap stored keys (remapping happens at name -> key time only).
export function catalogEntryForKey(gameKey) {
  if (!isCommunityGameKey(gameKey)) return null;
  const hit = CATALOG_BY_SLUG.get(gameKey.slice(COMMUNITY_GAME_PREFIX.length));
  return hit && hit.key === gameKey ? hit : null;
}

// True when this key is a curated game (browse-catalog extra or community-
// catalog entry) whose name/genre are canonical rather than creator-typed.
export function isKnownCommunityGame(gameKey) {
  return Boolean(extraGameFor(gameKey)) || Boolean(catalogEntryForKey(gameKey));
}

export function communityGameKeyFromName(raw) {
  const slug = communityGameSlug(raw);
  if (!slug) return null;
  const known = CATALOG_BY_SLUG.get(slug);
  if (known) return known.key;
  return `${COMMUNITY_GAME_PREFIX}${slug}`;
}

export function isCommunityGameKey(key) {
  return typeof key === 'string' && COMMUNITY_GAME_KEY_RE.test(key);
}

export function communityGameNameFromKey(key) {
  if (!isCommunityGameKey(key)) return null;
  return key
    .slice(COMMUNITY_GAME_PREFIX.length)
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Genre-aware (founder 2026-08-13): games match their real category, and
// modes follow the genre. Catalog genres are canonical; cg_* games use the
// creator's declared genre. type stays 'community' on purpose — the
// racing-type branches (heats, race leagues, GT7 lobby machinery) must
// never fire for open-catalog games.
export function buildCommunityGameProfile(gameKey, displayName, genre) {
  const extra = extraGameFor(gameKey);
  const catalogHit = extra ? null : catalogEntryForKey(gameKey);
  // Catalog names/genres are canonical - a client-supplied value may not
  // override them. The community-game catalog extends that rule to every
  // known game: "halo" filed under Fighting lands as Halo Infinite, Shooter.
  const name = (extra && extra.displayName)
    || (catalogHit && catalogHit.name)
    || normalizeCommunityGameName(displayName)
    || communityGameNameFromKey(gameKey)
    || String(gameKey || '').toUpperCase();
  const resolvedGenre = (extra && extra.genre)
    || (catalogHit && catalogHit.genre)
    || normalizeCommunityGenre(genre);
  // Racing games are solo; showdown (full-grid race, placement reports)
  // opens only where a payout-structure family exists (catalog racing
  // extras with a tuned grid size). Everything else runs h2h + brackets.
  const isRacingGenre = resolvedGenre === 'racing';
  const showdownGrid = isRacingGenre && extra && Number(extra.maxPlayers) >= 2
    ? Number(extra.maxPlayers)
    : null;
  const teamSizes = isRacingGenre ? [1] : [1, 2, 3, 4, 5, 6];
  return {
    displayName: name,
    abbr: (extra && extra.abbr) || null,
    type: 'community',
    isCommunity: true,
    genre: resolvedGenre,
    allowedModes: showdownGrid ? ['h2h', 'showdown'] : [...COMMUNITY_ALLOWED_MODES],
    // Catalog extras narrow to the platforms the game runs on (MKW is
    // Switch-only); cg_* typed games keep the full list — their genre
    // could narrow it later once demand shows which games they are.
    allowedPlatforms: [...((extra && extra.allowedPlatforms) || COMMUNITY_ALLOWED_PLATFORMS)],
    ...(showdownGrid ? { maxPlayers: showdownGrid } : {}),
    formats: {
      [isRacingGenre ? 'Standard Race' : 'Standard Match']: {
        description: isRacingGenre
          ? 'One race on settings the field agrees to. Report your finish when it ends.'
          : 'One match on settings both sides agree to. Report your result when it ends.',
        durationMinutes: isRacingGenre ? 20 : 30,
        teamSizes,
        defaultTeamSize: 1,
        ...(showdownGrid ? { maxPlayers: showdownGrid } : {}),
      },
    },
  };
}

export function resolveGameProfile(gameKey, gameName, genre) {
  if (!gameKey || typeof gameKey !== 'string') return null;
  if (GAME_PROFILES && GAME_PROFILES[gameKey]) return GAME_PROFILES[gameKey];
  if (extraGameFor(gameKey)) return buildCommunityGameProfile(gameKey, gameName, genre);
  if (isCommunityGameKey(gameKey)) return buildCommunityGameProfile(gameKey, gameName, genre);
  return null;
}

export function isOpenCatalogGame(gameKey) {
  if (!gameKey || (GAME_PROFILES && GAME_PROFILES[gameKey])) return false;
  return Boolean(extraGameFor(gameKey)) || isCommunityGameKey(gameKey);
}

export function gameUsesManualResults(gameKey) {
  if (!gameKey || typeof gameKey !== 'string') return false;
  if (gameKey === 'critcash_arena') return false;
  return !VISION_RESOLVE_GAMES.has(gameKey);
}

// Display label for an event's game anywhere in the app: catalog name,
// stored community name, or a prettified slug.
export function eventGameLabel(ev) {
  if (!ev) return '';
  const key = ev.game || ev.gameId || '';
  const profile = resolveGameProfile(key, ev.gameName);
  return (profile && profile.displayName) || ev.gameName || (key ? key.toUpperCase() : '');
}
